import React, {useCallback, useEffect, useState} from "react";
import {
	QuestionContainer,
	QuestionCounter,
	QuestionTitle,
	QuestionTitleContainer,
	QuestionWrapper,
} from "./common";
import styled from "styled-components";
import {first, partial, size} from "lodash";
import {getBGImage, getLogo, IBGImageProps} from "modules/utils";
import {BaseButton} from "components";
import {useStore} from "modules/hooks";
import {IQuestionProps} from "modules/types/question";
import {AuthButtons} from "components/Buttons/AuthButtons";

const Wrapper = styled(QuestionWrapper)`
	position: relative;
`;

const Container = styled(QuestionContainer)`
	padding: calc(5vh + 50px) 0 calc(4vh + 60px);

	@media (max-width: 840px) {
		padding: calc(5vh + 80px) 0 0;
	}
`;

const QuestionContentContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	overflow: hidden;
	box-sizing: border-box;
	padding: 0 5%;

	@media (max-width: 840px) {
		padding: 0 35px;
	}
`;

const ImagesList = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	z-index: 1;
	will-change: contents;
`;

const BGImage = styled.div<IBGImageProps>`
	background: ${getBGImage};
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	transition: all 1.5s;
	z-index: 1;
	opacity: 0;

	&.active {
		opacity: 1;
	}

	@media (max-width: 840px) {
		background: ${({imageSrc, imageSrcMobile}) =>
			getBGImage({imageSrc: imageSrcMobile || imageSrc})};
	}
`;

const OptionsList = styled.div`
	display: flex;
	margin-top: 16px;

	@media (max-width: 840px) {
		flex-direction: column;
		align-items: center;
	}
`;

const Option = styled(BaseButton)`
	height: auto;
	min-height: 52px;
	border-radius: 0;
	line-height: inherit;
	font-family: "Khand", sans-serif;
	width: 100%;
	color: ${({theme}) => theme.color.question1};
	background: ${({theme}) => theme.background.question1};
	padding: 17px;
	max-width: 200px;
	margin-right: 20px;
	font-weight: 700;
	border: 1px solid ${({theme}) => theme.background.question1};
	transition: background 0.5s;
	text-align: center;
	font-size: 16px;

	&:last-child {
		margin-right: 0;
	}

	&.active,
	&:hover {
		background: ${({theme}) => theme.background.question1Active};
	}

	@media (max-width: 1400px) {
		max-width: 183px;
	}

	@media (max-width: 840px) {
		max-width: 220px;
		margin: 0 0 20px;
	}

	@media (max-width: 500px) {
		max-width: 183px;
		margin: 0 0 20px;
	}
`;

const Title = styled.h1`
	margin: 20px 0 60px;
	font-size: 30px;
	font-weight: 700;
	line-height: 34px;
	text-align: center;

	.title {
		font-size: 36px;

		@media (max-width: 840px) {
			font-size: 30px;
		}
	}
`;

const Logo = styled.img`
	width: 350px;

	@media (max-width: 840px) {
		max-width: 250px;
	}
`;

const AUTO_SCROLL_INTERVAL_SEC = 3.5;

export const QuestionButton: React.FC<IQuestionProps> = ({
	title,
	options,
	id: questionID,
	questionNumber,
}) => {
	const {UIQuestionsProgress, I18n} = useStore();
	const noOfQuestions = UIQuestionsProgress.noOfQuestions;
	const [activeId, setActiveId] = useState<number | null>(null);
	const [autoTransitionActiveId, setAutoTransitionActiveId] = useState(first(options)?.id || 0);
	const autoTransition = activeId === null;
	const imagesSize = size(options);
	const logo = getLogo();

	const getActiveImageClass = useCallback(
		(id: number) => {
			if (autoTransition) {
				return id === autoTransitionActiveId ? "active" : "";
			}

			return id === activeId ? "active" : "";
		},
		[activeId, autoTransitionActiveId, autoTransition]
	);

	useEffect(() => {
		if (!autoTransition || !imagesSize) {
			return;
		}

		const timeoutID = setTimeout(() => {
			const currentIndex = options.findIndex(
				(option) => option.id === autoTransitionActiveId
			);
			const nextIndex = (currentIndex + 1) % options.length;

			setAutoTransitionActiveId(options[nextIndex].id);
		}, AUTO_SCROLL_INTERVAL_SEC * 1000);

		return () => {
			clearTimeout(timeoutID);
		};
	}, [autoTransition, imagesSize, autoTransitionActiveId, options]);

	const onSelectOption = useCallback(
		(optionId: number, questionId: number) => {
			setActiveId(optionId);
			UIQuestionsProgress.selectAnswer(questionId, optionId);
		},
		[setActiveId, UIQuestionsProgress]
	);

	const getActiveOptionClass = useCallback(
		(id: number) => (id === activeId ? "active" : ""),
		[activeId]
	);
	// TODO add text with loco

	return (
		<Wrapper>
			<ImagesList>
				{options.map(({image, id, mobile_image}) => (
					<BGImage
						className={getActiveImageClass(id)}
						imageSrc={image}
						imageSrcMobile={mobile_image}
						key={id}
					/>
				))}
			</ImagesList>
			<Container>
				<QuestionContentContainer>
					<Logo src={logo} alt="CFL logo" />
					<Title>
						<span className="title">{I18n.t("global.header.title")}</span>
						<br />
						<span>{I18n.t("global.header.welcome")}</span>
					</Title>
					{!UIQuestionsProgress.isLoggedIn && <AuthButtons />}
				</QuestionContentContainer>

				<QuestionContainer>
					<QuestionTitleContainer>
						<QuestionCounter>
							Q{questionNumber}/{noOfQuestions}
						</QuestionCounter>
						<QuestionTitle>{title}</QuestionTitle>
					</QuestionTitleContainer>

					<OptionsList>
						{options.map(({id, title}) => (
							<Option
								key={id}
								className={getActiveOptionClass(id)}
								onClick={partial(onSelectOption, id, questionID)}>
								{title}
							</Option>
						))}
					</OptionsList>
				</QuestionContainer>
			</Container>
		</Wrapper>
	);
};
